// Players (spec/14 §10, F-HQ-28): HQ looks a person up across the Platform. It does not browse the
// Platform's players — `GET /v1/hq/players` needs `q` — so the page is a search box and, once
// something is searched for, what it found: the profile basics, the contact details in full (the
// product owner, 2026-09-22) and whether a platform ban stops them now. Each result opens the
// player's platform record (`PlayerPage.tsx`).
//
// No typed reason is asked for (the product owner, 2026-09-22): the server records every search in
// one platform-level row, and the page says so. What was searched for is kept in the section's
// state, not in the address, so a phone number or an email address never lands in the browser's
// history or a CDN log; coming back from a player finds the same results.

import type { HqPlayerList, HqPlayerSummary } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, InfoTip, Table, TextField, type TableColumn } from '@clavian/ui-web';
import { TableSkeleton } from '../../ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { queryKeys } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_PLAYERS_READ } from '../sections';
import { fullName, playerName, SEARCH_MAX, SEARCH_MIN } from './names';

interface PlayerSearchState {
  readonly q: string;
  readonly setQ: (q: string) => void;
}

const PlayerSearchContext = createContext<PlayerSearchState | null>(null);

function usePlayerSearch(): PlayerSearchState {
  const state = useContext(PlayerSearchContext);
  if (state === null) throw new Error('usePlayerSearch() is for pages inside the Players section.');
  return state;
}

/** `/players/*`: the section's frame. It holds the search, so a player's page can come back to it. */
export function PlayersSection(): ReactNode {
  const [q, setQ] = useState('');
  const value = useMemo(() => ({ q, setQ }), [q]);
  return (
    <RequirePlatformPermission permission={HQ_PLAYERS_READ}>
      <PlayerSearchContext.Provider value={value}>
        <Outlet />
      </PlayerSearchContext.Provider>
    </RequirePlatformPermission>
  );
}

/** `/players`: the search box and what it found. */
export function PlayerSearchPage(): ReactNode {
  const { t } = useTranslation('hq');
  const { q, setQ } = usePlayerSearch();
  const [text, setText] = useState(q);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <div className="console-page">
      <div className="console-hq-heading">
        <h1 className="console-page__title">{t('players.title')}</h1>
        <InfoTip>
          {t('players.intro')} {t('players.audited')}
        </InfoTip>
      </div>
      <form
        className="console-hq-search"
        role="search"
        aria-label={t('players.search.name')}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const wanted = text.trim();
          const refused =
            wanted.length < SEARCH_MIN
              ? t('players.search.tooShort', { count: SEARCH_MIN })
              : wanted.length > SEARCH_MAX
                ? t('players.search.tooLong', { count: SEARCH_MAX })
                : undefined;
          setError(refused);
          if (refused === undefined) setQ(wanted);
        }}
      >
        <TextField
          label={t('players.search.label')}
          description={t('players.search.description')}
          value={text}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setText(event.target.value)}
          {...(error === undefined ? {} : { error })}
        />
        <Button type="submit" variant="primary">
          {t('players.search.submit')}
        </Button>
      </form>
      {q === '' ? null : <SearchResults q={q} />}
    </div>
  );
}

function SearchResults({ q }: { readonly q: string }) {
  const { t } = useTranslation('hq');
  const api = useApi();
  const format = useVenueFormatter();
  const search = useInfiniteQuery({
    queryKey: queryKeys.hqPlayerSearch(q),
    queryFn: ({ pageParam }): Promise<HqPlayerList> =>
      api.listHqPlayers({ query: { q, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    // Every read is a row in the platform's audit log: read again when asked, not when the tab regains focus.
    refetchOnWindowFocus: false,
  });

  const columns = useMemo<readonly TableColumn<HqPlayerSummary>[]>(
    () => [
      {
        key: 'player',
        header: t('players.column.player'),
        cell: (player) => (
          <div className="console-hq-person">
            <Avatar name={playerName(player)} size="sm" decorative />
            <div className="console-hq-cell">
            <Link to={HQ_PATHS.player} params={playerParams(player.id)} className="console-hq-name__link">
              {playerName(player)}
            </Link>
            {player.username !== null && player.username !== '' ? <span className="console-muted">@{player.username}</span> : null}
            {player.displayName !== null && player.displayName !== '' && fullName(player) !== null ? (
              <span className="console-muted">{player.displayName}</span>
            ) : null}
            </div>
          </div>
        ),
      },
      { key: 'phone', header: t('players.column.phone'), cell: (player) => <Contact value={player.phoneNumber} /> },
      { key: 'email', header: t('players.column.email'), cell: (player) => <Contact value={player.email} /> },
      {
        key: 'ban',
        header: t('players.column.ban'),
        cell: (player) =>
          player.banned ? (
            <Badge tone="danger" dot>
              {t('players.banned')}
            </Badge>
          ) : <span className="console-muted">{t('players.notBanned')}</span>,
      },
      { key: 'joined', header: t('players.column.joined'), cell: (player) => format.date(player.createdAt, 'medium') },
    ],
    [t, format],
  );

  if (search.data === undefined) {
    return search.isError ? (
      <LoadFailure error={search.error} message={t('players.search.failed')} onRetry={() => void search.refetch()} />
    ) : (
      <TableSkeleton />
    );
  }

  const players = search.data.pages.flatMap((page) => page.data);
  return (
    <section className="console-hq-results" aria-labelledby="console-hq-results-title">
      <h2 id="console-hq-results-title">{t('players.search.results')}</h2>
      <p role="status">
        {search.hasNextPage
          ? t('players.search.foundMore', { count: players.length })
          : t('players.search.found', { count: players.length, q })}
      </p>
      <Table
        caption={t('players.search.caption', { q })}
        captionHidden
        columns={columns}
        rows={players}
        rowKey={(player) => player.id}
        // A platform ban stops a player now: the one row that takes the danger rail and tint (ADR-0043).
        rowTone={(player) => (player.banned ? 'danger' : undefined)}
        empty={t('players.search.none', { q })}
      />
      {search.hasNextPage ? (
        <Button className="console-hq-more" busy={search.isFetchingNextPage} onClick={() => void search.fetchNextPage()}>
          {t('players.search.more')}
        </Button>
      ) : null}
    </section>
  );
}

/** HQ's paths are plain strings (paths.ts), so their params are too. */
const playerParams = (playerId: string): Readonly<Record<string, string>> => ({ playerId });

/** A phone number or an email address, in full, as HQ is sent it; "Not given" where there is none. */
function Contact({ value }: { readonly value: string | null }) {
  const { t } = useTranslation('hq');
  return value === null ? <span className="console-muted">{t('players.notGiven')}</span> : <span className="console-hq-subject">{value}</span>;
}
